import { newIdempotencyKey } from '@micarahr/api-client';
import type {
  ApprovalItem,
  PermissionCatalogGroup,
  RoleDefinition,
  SystemUser,
  SystemUserStatus,
} from '@micarahr/shared-types';
import { apiTransport } from './api-client';
import type { Page } from './collections';
import { narrow, USER_ROLES } from './narrow';

/**
 * A login just created: the temporary password while passwords exist (shown
 * once), or null where they do not — and whether a sign-in link was emailed.
 */
export interface CreatedLogin {
  user: SystemUser;
  temporaryPassword: string | null;
  invitationSent: boolean;
}

/**
 * User and role administration.
 *
 * The screen offers three destructive things — suspend, deactivate, change
 * roles — and this adapter deliberately does none of the checking for them.
 * The last-administrator rule and the self-lockout rule live on the server,
 * inside the transaction that does the write, and the reason is that a browser
 * cannot enforce either one: the count of remaining administrators can change
 * between the page loading and the button being pressed, and a second
 * administrator in another tab is exactly the case that produces two
 * simultaneous removals.
 *
 * So the screen calls, and shows the refusal. That is also why the confirm
 * dialog says "if this is the last active administrator, the change will be
 * refused" rather than hiding the button: a guard nobody can trigger is a
 * guard nobody has seen work.
 *
 * Every mutation carries a reason. Not ceremony — "who removed the payroll
 * administrator role in March and why" is the question an audit asks, and half
 * an answer names a suspect without a motive.
 */

interface ApiUser {
  id: string;
  email: string;
  displayName: string;
  status: string;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
  employeeId: string | null;
  employee: { id: string; fullName: string; employeeNumber: string } | null;
  roles: { role: { key: string; name: string; isSystem: boolean } }[];
  scopes: { kind: string; targetId: string }[];
  mfaEnabled: boolean;
  lastMobileSignInAt: string | null;
}

interface ApiRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  permissions: string[];
  effectivePermissions: string[];
  editable: boolean;
  lockedReason: string | null;
}

interface ApiPermission {
  key: string;
  label: string;
  module: string;
  moduleLabel: string;
  moduleDescription: string;
  description: string;
  sensitive: boolean;
  implies: string[];
}

/**
 * An account's state, including the one the server derives rather than stores.
 *
 * `locked` is not a column: it is a live failed-sign-in throttle, and it
 * outranks the stored status on the screen because "cannot sign in right now"
 * is the more useful fact when somebody is on the phone asking why.
 */
function toStatus(row: ApiUser): SystemUserStatus {
  if (row.lockedUntil && new Date(row.lockedUntil) > new Date()) return 'locked';

  switch (row.status) {
    case 'invited':
      // Signed in with the one-time password: the invitation is taken up and
      // what is outstanding is the password change.
      return row.lastLoginAt ? 'password_pending' : 'invited';
    case 'active':
    case 'suspended':
    case 'deactivated':
      return row.status;
    default:
      // Not narrow(): a status this client does not know should not take the
      // whole user list down, and `invited` is the least consequential
      // reading — it grants nothing and prompts somebody to look.
      return 'invited';
  }
}

function toSystemUser(row: ApiUser): SystemUser {
  const roles = row.roles.map((assignment) => assignment.role);

  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? null,
    employeeNumber: row.employee?.employeeNumber ?? null,
    displayName: row.displayName,
    email: row.email,
    // Platform roles and this organisation's own roles are different lists on
    // the screen, and `isSystem` is what separates them.
    roles: roles
      .filter((role) => role.isSystem)
      .map((role) => narrow(USER_ROLES, role.key, 'role')),
    customRoleIds: roles.filter((role) => !role.isSystem).map((role) => role.key),
    status: toStatus(row),
    scopedDepartmentIds: row.scopes
      .filter((scope) => scope.kind === 'department')
      .map((scope) => scope.targetId),
    lastSignInAt: row.lastLoginAt,
    mfaEnabled: row.mfaEnabled,
    lastMobileSignInAt: row.lastMobileSignInAt,
    requirePasswordChange: row.mustChangePassword,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

function toRoleDefinition(row: ApiRole): RoleDefinition {
  return {
    id: row.id,
    key: row.key,
    name: row.name,
    description: row.description ?? '',
    system: row.isSystem,
    active: row.isActive,
    /*
     * The server's own keys, one per checkbox.
     *
     * This used to be translated into the web's coarse menu vocabulary, where
     * one box stood for several server keys and several boxes for one — so
     * "Manage work locations" and "Manage geofences" were the same grant, and
     * the Employee role showed two permissions while the server stored five.
     *
     * The effective set rather than the stored one: it differs only where a
     * stored grant implies another the row does not repeat (`users.deactivate`
     * → `users.view`), and the implied key is genuinely held — the checker
     * enforces it — so the editor shows it, marked as included.
     */
    permissions: [...row.effectivePermissions].sort(),
    effectivePermissions: row.effectivePermissions,
    lockedReason: row.editable ? null : (row.lockedReason ?? 'This role cannot be edited.'),
    userCount: row.userCount,
    createdAt: '',
    updatedAt: '',
    clonedFromRoleId: null,
  };
}

const cursors = new Map<string, string[]>();

export const apiUserAdminService = {
  async list(
    filter: { status?: string; search?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<SystemUser>> {
    const pageSize = filter.pageSize ?? 25;
    const pageNumber = filter.page ?? 1;
    const key = JSON.stringify([filter.status, filter.search, pageSize]);

    const known = cursors.get(key) ?? [];
    const cursor = pageNumber <= 1 ? undefined : known[Math.min(pageNumber - 2, known.length - 1)];

    const page = await apiTransport().page<ApiUser>('/api/v1/users', {
      query: {
        status: filter.status === 'all' ? undefined : filter.status,
        search: filter.search,
        limit: pageSize,
        cursor,
      },
    });

    if (page.pageInfo.nextCursor) {
      const next = [...known];
      next[pageNumber - 1] = page.pageInfo.nextCursor;
      cursors.set(key, next);
    }

    return {
      rows: page.items.map(toSystemUser),
      total: page.totalCount,
      page: pageNumber,
      pageSize,
    };
  },

  async get(id: string): Promise<SystemUser | null> {
    try {
      return toSystemUser(await apiTransport().get<ApiUser>(`/api/v1/users/${id}`));
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  },

  summary(): Promise<{
    total: number;
    active: number;
    invited: number;
    /** Invited accounts signed in to with the one-time password, not yet replaced. */
    passwordPending: number;
    suspended: number;
    deactivated: number;
    /** Accounts with a confirmed second factor. Null only on the prototype. */
    mfaEnabled: number | null;
  }> {
    return apiTransport().get('/api/v1/users/summary');
  },

  /**
   * Change an account's state.
   *
   * No client-side last-administrator check, on purpose. The server owns it,
   * runs it inside the write's transaction, and records the refusal — three
   * things a browser cannot do. Duplicating the rule here would produce a
   * second answer that is wrong under exactly the conditions the rule exists
   * for.
   */
  async setStatus(
    id: string,
    status: 'active' | 'suspended' | 'deactivated',
    reason: string,
  ): Promise<SystemUser> {
    return toSystemUser(
      await apiTransport().patch<ApiUser>(`/api/v1/users/${id}/status`, { status, reason }),
    );
  },

  async setRoles(id: string, roles: string[], reason: string): Promise<SystemUser> {
    return toSystemUser(
      await apiTransport().put<ApiUser>(`/api/v1/users/${id}/roles`, { roles, reason }),
    );
  },

  /**
   * Create a sign-in for an employee.
   *
   * The temporary password in the result exists nowhere else — the server
   * keeps only its hash — so the screen shows it once and never stores it.
   * With passwords off (production) there is none: the account signs in by
   * emailed link, and `sendInvitation` emails the first one.
   * Keyed, so a retried request after a timeout cannot mint a second password
   * the administrator never saw.
   */
  async createLogin(
    employeeId: string,
    roles: string[],
    reason: string,
    sendInvitation = false,
  ): Promise<CreatedLogin> {
    const created = await apiTransport().post<{
      user: ApiUser;
      temporaryPassword: string | null;
      invitationSent?: boolean;
    }>(
      '/api/v1/users',
      { employeeId, roles, reason, sendInvitation },
      { idempotencyKey: newIdempotencyKey() },
    );
    return {
      user: toSystemUser(created.user),
      temporaryPassword: created.temporaryPassword,
      invitationSent: created.invitationSent ?? false,
    };
  },

  /**
   * Replace an account's password with a new temporary one, shown once.
   *
   * Keyed for the same reason as `createLogin`: a retry after a timeout must
   * not mint a second password — and end the first one's sessions — behind
   * the one the administrator is looking at.
   */
  async issueTemporaryPassword(
    id: string,
    reason: string,
  ): Promise<{ user: SystemUser; temporaryPassword: string }> {
    const issued = await apiTransport().post<{ user: ApiUser; temporaryPassword: string }>(
      `/api/v1/users/${id}/temporary-password`,
      { reason },
      { idempotencyKey: newIdempotencyKey() },
    );
    return { user: toSystemUser(issued.user), temporaryPassword: issued.temporaryPassword };
  },
};

export const apiRolePermissionService = {
  async list(): Promise<RoleDefinition[]> {
    const rows = await apiTransport().get<ApiRole[]>('/api/v1/roles');
    return rows.map(toRoleDefinition);
  },

  async get(id: string): Promise<RoleDefinition | null> {
    const rows = await apiRolePermissionService.list();
    return rows.find((role) => role.id === id) ?? null;
  },

  /**
   * The permission catalog, from the server, grouped by module.
   *
   * Not a copy kept in the frontend: a permission that exists in a picker and
   * not in the checker is one an administrator grants believing it does
   * something. Labels, sensitive markers and implications come with it, so
   * every key the server can grant has a checkbox with a readable name.
   */
  async catalog(): Promise<PermissionCatalogGroup[]> {
    const rows = await apiTransport().get<ApiPermission[]>('/api/v1/permissions');
    const groups = new Map<string, PermissionCatalogGroup>();
    for (const row of rows) {
      const group = groups.get(row.module) ?? {
        key: row.module,
        label: row.moduleLabel,
        description: row.moduleDescription,
        permissions: [],
      };
      group.permissions.push({
        key: row.key,
        label: row.label,
        description: row.description,
        sensitive: row.sensitive,
        implies: row.implies,
      });
      groups.set(row.module, group);
    }
    return [...groups.values()];
  },

  /** The permission the server checks before any role edit. */
  editPermission: 'users.manage_roles',

  /** How many accounts a change would affect. Counted by the server. */
  async impactOf(roleKey: string): Promise<number> {
    const roles = await apiRolePermissionService.list();
    return roles.find((role) => role.key === roleKey)?.userCount ?? 0;
  },

  /**
   * Save exactly the keys the editor shows as granted.
   *
   * The PUT replaces the role's grants outright, and the editor now holds
   * every key the role has — one checkbox each, nothing it cannot display —
   * so the ticked set is the whole request. Nothing is carried along unseen.
   */
  async setPermissions(id: string, permissions: string[], reason: string): Promise<RoleDefinition> {
    return toRoleDefinition(
      await apiTransport().put<ApiRole>(`/api/v1/roles/${id}/permissions`, {
        permissions: [...new Set(permissions)].sort(),
        reason,
      }),
    );
  },

  async setActive(id: string, isActive: boolean, reason: string): Promise<RoleDefinition> {
    return toRoleDefinition(
      await apiTransport().patch<ApiRole>(`/api/v1/roles/${id}/status`, { isActive, reason }),
    );
  },
};

/**
 * The approval queue.
 *
 * One call, not three. The prototype swept leave, overtime and corrections
 * separately and re-derived "what is pending" from three status fields; the
 * server reads its `ApprovalRequest` table, which is the queue — it carries
 * the current step, and on that step the permission and relationship a decider
 * must stand in.
 *
 * `assigned` therefore means *what this person can actually decide*, decided
 * by the server. A manager shown a request sitting at HR review has been told
 * something false about whose turn it is, and a manager shown another
 * manager's report has been shown the reason somebody gave for their leave.
 * Neither is a filter a browser can be trusted to apply, because applying it
 * means knowing the authorization model.
 */
interface ApiApprovalItem {
  id: string;
  kind: string;
  recordId: string;
  referenceCode: string;
  title: string;
  subjectEmployeeId: string | null;
  subjectName: string;
  submittedByName: string;
  submittedAt: string;
  magnitude: string;
  status: string;
  currentStepLabel: string;
  currentStepIndex: number;
  dueDate: string | null;
  flag: string | null;
  completedAt?: string | null;
  decidedByName?: string | null;
  steps?: {
    label: string;
    status: string;
    decidedAt: string | null;
    decidedByName: string | null;
    comment: string | null;
  }[];
  /* An attendance correction's times, reason and explanation. */
  correction?: ApprovalCorrectionFacts;
}

/** What an attendance correction asks for: the times on record and the times wanted. */
export interface ApprovalCorrectionFacts {
  reason: string;
  explanation: string;
  currentTimeIn: string | null;
  currentTimeOut: string | null;
  requestedTimeIn: string | null;
  requestedTimeOut: string | null;
}

export const apiApprovalService = {
  async list(
    filter: { kind?: string; view?: 'assigned' | 'hr' | 'payroll' | 'completed' | 'overdue' } = {},
  ): Promise<ApprovalItem[]> {
    /*
     * The prototype's views and the server's are not the same set.
     *
     * `hr` and `payroll` were "requests whose status happens to be pending_hr"
     * — a guess at whose turn it is, made from a status field. The server
     * answers the real question through step eligibility, so both map to the
     * organisation-wide view and the step label carries the detail.
     */
    /*
     * `hr` is the server's `hr_review`: requests sitting at an HR step. It
     * was sent as `all_open`, the organisation-wide management view, which an
     * HR officer is not granted — so their "HR review" tab was a refusal.
     */
    const view =
      filter.view === 'completed' || filter.view === 'overdue'
        ? filter.view
        : filter.view === 'hr'
          ? 'hr_review'
          : filter.view === 'assigned' || filter.view === undefined
            ? 'assigned'
            : 'all_open';

    const rows = await apiTransport().get<ApiApprovalItem[]>('/api/v1/approvals', {
      query: { view, kind: filter.kind === 'all' ? undefined : filter.kind },
    });

    return rows.map((row) => ({
      id: row.id,
      kind: row.kind as ApprovalItem['kind'],
      recordId: row.recordId,
      referenceCode: row.referenceCode,
      title: row.title,
      subjectEmployeeId: row.subjectEmployeeId,
      subjectName: row.subjectName,
      submittedByName: row.submittedByName,
      submittedAt: row.submittedAt,
      magnitude: row.magnitude,
      status: row.status as ApprovalItem['status'],
      currentStepLabel: row.currentStepLabel,
      // The server does not assign a request to one person: a step names a
      // permission and sometimes a relationship, and several people can hold
      // both. Null says "not assigned to an individual" rather than inventing
      // an owner the queue would then filter by.
      assignedToUserId: null,
      dueDate: row.dueDate,
      flag: row.flag,
      completedAt: row.completedAt ?? null,
      decidedByName: row.decidedByName ?? null,
      steps: row.steps ?? [],
      correction: row.correction ?? null,
    }));
  },
};
